package com.milta.feature.importjobs.domain.entity

import com.milta.feature.imports.domain.entity.ImportPdfResult
import com.milta.feature.ocrscan.domain.entity.OcrScanResult
import com.milta.feature.spreadsheetimport.domain.entity.SpreadsheetImportResult

/**
 * One asynchronous AI import (`ImportJobDto`): the file is already on the server, the extraction
 * runs in the background. [result] is only present when [ImportJobStatus.hasResult].
 */
data class ImportJob(
    val id: String,
    val flow: ImportJobFlow,
    val status: ImportJobStatus,
    /** Raw backend code of a failed job — mapped to screen copy later, not here. */
    val errorCode: String?,
    val attemptCount: Int,
    val fileName: String,
    /** ISO-8601 with offset — the analysis screen's chrono runs from it. */
    val createdAt: String,
    /** ISO-8601, null until the job changed once. */
    val updatedAt: String?,
    /** Existing route the import was launched from — its deliveries always go there. Null = new route being created. */
    val routeId: String?,
    /** `yyyy-MM-dd` of [routeId]'s route, null with it. */
    val routeDate: String?,
    /** Document already read before a failure: a retry resumes at the address check. */
    val extractionCompleted: Boolean,
    val result: ImportJobResult?,
    // ── Real progress (retour 26/10) — all null with a server that doesn't send them yet ──
    /** Current step; null while Pending (or with an older server). */
    val stage: ImportJobStage? = null,
    /** Start of [stage], epoch ms. */
    val stageStartedAtEpochMillis: Long? = null,
    /** How long the reading took — set once, kept when a relaunch resumes at the address check. */
    val readingDurationSeconds: Int? = null,
    /** Deliveries (or spreadsheet rows) found once the document was read. */
    val deliveryCount: Int? = null,
    /** Addresses already checked, out of [geocodeTotal]. */
    val geocodedCount: Int? = null,
    val geocodeTotal: Int? = null,
) {
    /** A failed job can always be relaunched without re-sending the file (retour 27/10: no attempt
     * limit, the AI credit controls the cost — at 0 the server refuses with `ai_credit_exhausted`). */
    val canRetry: Boolean get() = status == ImportJobStatus.FAILED
}

/** The result of a finished job — exactly the payload of the former synchronous endpoint of each flow. */
sealed interface ImportJobResult {
    data class Pdf(val value: ImportPdfResult) : ImportJobResult
    data class Spreadsheet(val value: SpreadsheetImportResult) : ImportJobResult
    data class OcrDeliveryList(val value: OcrScanResult) : ImportJobResult
}
